<template>
  <!-- ElCarousel legt seine Folien absolut übereinander und braucht deshalb eine Höhe.
       Auf dem Handy gibt der Drawer sie über seine `size` vor, der Dialog auf dem
       Desktop nicht — dort steht sie hier. -->
  <div
    ref="swipeTarget"
    class="flex flex-col h-full md:h-[60vh] md:max-h-[30rem] -mx-4 md:-mx-6 px-4 md:px-6"
  >
    <ElCarousel
      ref="carouselRef"
      height="100%"
      class="grow min-h-0"
      :autoplay="false"
      arrow="never"
      indicator-position="none"
      @change="(index: number) => slideIndex = index"
    >
      <ElCarouselItem
        v-for="item in items"
        :key="item.title"
        class="overflow-y-auto"
      >
        <SvgVue
          class="mx-auto mb-8 md:mb-10"
          :icon="item.icon"
        />
        <h3 class="text-xl md:text-2xl text-center font-semibold leading-6 text-gray-900 m-0">
          {{ item.title }}
        </h3>
        <p class="text-sm md:text-base text-center leading-6 text-gray-600 mt-3 mb-5 md:my-5">
          {{ item.description }}
        </p>
      </ElCarouselItem>
    </ElCarousel>

    <div class="md:hidden flex flex-row w-full justify-center gap-3 mb-4">
      <button
        v-for="(item, index) in items"
        :key="item.title"
        class="w-3 h-3 md:w-2 md:h-2 rounded-full"
        :aria-label="`Navigationsschritt ${index + 1}: ${item.title}`"
        :class="[slideIndex === index ? 'bg-blue-500' : 'bg-gray-300/50']"
        @click="carouselRef?.setActiveItem(index)"
      />
    </div>
    <div class="md:hidden flex flex-row">
      <AppButton
        v-if="slideIndex + 1 === items.length"
        type="primary"
        class="w-full"
        @click="$emit('close')"
      >
        Schließen
      </AppButton>
    </div>

    <div class="hidden md:flex flex-row w-full items-center justify-end gap-4">
      <AppButton
        v-if="slideIndex > 0"
        type="primary"
        text
        bg
        @click="carouselRef?.prev()"
      >
        Zurück
      </AppButton>
      <AppButton
        v-if="slideIndex + 1 < items.length"
        type="primary"
        @click="carouselRef?.next()"
      >
        Weiter
      </AppButton>
      <AppButton
        v-if="slideIndex + 1 === items.length"
        type="primary"
        @click="$emit('close')"
      >
        Schließen
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import AppButton from '@/application/components/ui/AppButton.vue';
import { ElCarousel, ElCarouselItem } from 'element-plus';
import { useSwipe } from '@vueuse/core';
import { ref } from 'vue';

import SvgVue from "@/application/components/SvgVue";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

defineEmits(['close', 'open']);

const { duzen } = useBaseDataStore();

const carouselRef = ref<InstanceType<typeof ElCarousel> | null>(null);
const slideIndex = ref(0);
const swipeTarget = ref<HTMLElement | null>(null);

// ElCarousel has no swipe gesture, but onboarding runs mostly on mobile.
useSwipe(swipeTarget, {
  onSwipeEnd: (_event, direction) => {
    if (direction === 'left') carouselRef.value?.next();
    if (direction === 'right') carouselRef.value?.prev();
  },
});

interface Item {
  title: string;
  description: string;
  icon: string;
}

const items: Item[] = [
  {
    icon: 'onboarding/onboarding_01',
    title: 'Willkommen im Finanzmanager',
    description: `Mit dem Finanzmanager ${duzen('kannst du deinem', 'können Sie Ihrem')} Makler Informationen `
      + 'bereitstellen, um bestmöglich und bedarfsgerecht beraten zu werden.'
      + 'Der Finanzmanager besteht aus den Bereichen Checkup, Bedarf, Dokumente, Verträge und meine Daten. '
      + `Um diese Informationen noch einmal aufrufen, ${duzen('gehe', 'gehen Sie')} im Menü auf "Hinweise" und `
      + 'anschließend oben rechts auf die Einführung.',
  },
  {
    icon: 'onboarding/onboarding_02',
    title: 'Checkup',
    description: `Im Checkup ${duzen('kannst du deinem', 'können Sie Ihrem')} Makler Informationen zu Ihrer
    Lebenssituation bereitstellen bzw. diese aktualisieren. Mit diesen Informationen kann
    ${duzen('dein Makler dich', 'Sie ihr Makler')} umfassend beraten. Das Ausfüllen dauert ungefähr 3 Minuten.`,
  },
  {
    icon: 'onboarding/onboarding_03',
    title: 'Bedarf',
    description: `In diesem Bereich kann ${duzen('dir dein', 'Ihnen Ihr')} Makler Hintergrundinformationen zu
    verschiedenen Produktsparten freischalten. Dort ${duzen('erhältst du', 'erhalten Sie')} Informationen zu
    Versorgungslücken und Möglichkeiten diese zu schließen. Die dargestellten Informationen helfen bei der gemeinsamen
    Entscheidungsfindung.`,
  },
  {
    icon: 'onboarding/onboarding_04',
    title: 'Verträge',
    description: `Die Vertragsseite bietet eine Übersicht ${duzen('deiner', 'Ihrer')} Verträge und ermöglicht
    das Anlegen neuer Verträge.`,
  },
  {
    icon: 'onboarding/onboarding_05',
    title: 'Dokumente',
    description: `In diesem Bereich ${duzen('kannst du für deinen', 'können Sie für Ihren')} Makler Dokumente
    hochladen. Diese können auch Verträgen zugeordnet werden. Beim Upload von mehreren Bildern bzw.
    Fotos ${duzen('hast du', 'haben Sie')} die Möglichkeit, diese als PDF gebündelt bereitzustellen.`,
  },
  {
    icon: 'banking/multibanking',
    title: 'Multibanking',
    description: `In diesem Bereich ${duzen('kannst du deine', 'können Sie Ihre')} Bankverbindungen
    hinzufügen, um einen vollständigen Überblick über deine Finanzen zu haben oder automatisch
    ${duzen('deine', 'Ihre')} Versicherungsverträge zu übernehmen.`,
  },
  {
    icon: 'onboarding/onboarding_06',
    title: 'Mein Account',
    description: `Hier ${duzen('kannst du deinen', 'können Sie Ihren')} Zugang mit einer
    2-Faktor-Authentifizierung schützen sowie das Passwort ändern. `,
  },
  {
    icon: 'onboarding/onboarding_07',
    title: 'Meine Daten',
    description: `Unter Meine Daten ${duzen('kannst du deine', 'können Sie Ihre')} Kontaktdaten wie E-Mail,
    Telefonnummer und Adresse ändern.`,
  },
];
</script>
